import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FC } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useRtl } from "@ryde/redux";
import {
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  DashboardGraphColorMappingEnum,
  DATA_E2E_KEY,
  IBarGraphContent,
  IBarGraphSettings,
  IStyledAttributes,
  maxValueSuggestedMaxHelper,
  pickGraphColors,
} from "@ryde/shared";
import { rydeColors, rydeColorsGraphs } from "@ryde/theme";

const BarContainerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "dashboard-card-bar-container",
})`
  height: 93%;
  width: 100%;
  align-self: center;
  padding-bottom: 5px;
  padding-top: 5px;
`;

export interface IBarGraphContentProps {
  cardType: DashboardCardTemplateTypeEnum;
  cardName: DashboardCardNameEnum;
  content: IBarGraphContent;
  colorScheme: DashboardGraphColorMappingEnum;
  settings: IBarGraphSettings;
}

interface IAxisLabelStyle {
  color?: string;
  font?: {
    size?: number;
  };
}

interface ITitleAxisLabelStyle {
  title: {
    display?: boolean;
    text?: string;
    color?: string;
    font?: {
      size?: number;
    };
  };
}

export const CardBarGraphContentTemplate: FC<IBarGraphContentProps> = ({
  cardType = DashboardCardTemplateTypeEnum.VERTICAL_BARS,
  cardName = DashboardCardNameEnum.RIDES_SUPPLIERS,
  colorScheme,
  content,
  settings,
}) => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
  );

  const sortedContent = settings.sorter(content, settings.maxDisplay);

  const { t } = useTranslation();

  const { isRtl } = useRtl();
  const isHorizontal =
    cardType === DashboardCardTemplateTypeEnum.HORIZONTAL_BARS;

  const colors = pickGraphColors(
    cardName,
    colorScheme,
    "subLabel",
    settings.fieldValueToGetSecondaryValue,
    sortedContent.entities,
    settings.predicateFunctionColor,
    settings?.preferredColorNumberForMainColor,
  );

  const [xLabel, yLabel]: [string, string] = [
    t(`dashboard.${cardName}.X_LABEL`),
    t(`dashboard.${cardName}.Y_LABEL`),
  ];
  const subLabels: string[] = [];
  const values: number[] = [];

  sortedContent.entities?.forEach(ele => {
    const { subLabel, value } = ele;

    if (!subLabels.includes(subLabel)) {
      subLabels.push(subLabel);
      values.push(value);
    }
  });

  const axisLabelStyle: IAxisLabelStyle = {
    color: rydeColorsGraphs.barLabelTextColor,
    font: {
      size: 16,
    },
  };

  const buildTitle = (text: string): ITitleAxisLabelStyle => {
    return {
      title: {
        display: true,
        text,
        ...axisLabelStyle,
      },
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options: ChartOptions<any> = {
    indexAxis: isHorizontal ? "y" : "x",
    scales: {
      x: {
        reverse: isRtl,
        ...buildTitle(xLabel),

        ...(isHorizontal
          ? {
              suggestedMax: maxValueSuggestedMaxHelper(
                values.length !== 0 ? Math.max(...values) : 0,
              ),
              beginAtZero: true,
            }
          : {}),
      },
      y: {
        position: isRtl ? "right" : "left",
        ...buildTitle(yLabel),
        ...(!isHorizontal
          ? {
              suggestedMax: maxValueSuggestedMaxHelper(
                values.length !== 0 ? Math.max(...values) : 0,
              ),
              beginAtZero: true,
            }
          : {}),
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: rydeColors.white,
        textAlign: "center",
        font: {
          size: 12,
        },
      },
    },
  };
  const data = {
    labels: subLabels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: colors,
      },
    ],
  };

  return (
    <BarContainerStyled>
      <Bar data={data} options={options} />
    </BarContainerStyled>
  );
};
